import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  TextInput,
  FlatList,
  TouchableOpacity,
  StyleSheet,
  Modal,
  Button,
  Alert,
  Image,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { fetchAuthSession } from 'aws-amplify/auth';
import { apiUrl } from './utils/config';
import { User } from './types';

const AdminUserManagementPage = () => {
  const navigation = useNavigation();
  const [users, setUsers] = useState<User[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [tokenChange, setTokenChange] = useState('');
  const [modalVisible, setModalVisible] = useState(false);

  const getAuthToken = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      return session?.tokens?.idToken || null;
    } catch (error) {
      console.error('Error retrieving auth token:', error);
      return null;
    }
  }, []);

  const fetchUsers = async () => {
    try {
      const authToken = await getAuthToken();
      if (!authToken) throw new Error('Unable to retrieve auth token');

      const response = await fetch(`${apiUrl}admin/get-users?query=${searchQuery}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch users');

      const data: User[] = await response.json();
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleUserSelect = (user: User) => {
    setSelectedUser(user);
    setModalVisible(true);
  };

  const handleTokenUpdate = async () => {
    try {
      const authToken = await getAuthToken();
      if (!authToken) throw new Error('Unable to retrieve auth token');

      const response = await fetch(`${apiUrl}tokens/spend-tokens`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          user_id: selectedUser?.user_id,
          token_change: parseInt(tokenChange),
        }),
      });

      if (!response.ok) throw new Error('Failed to update tokens');

      Alert.alert('Success', 'Tokens updated successfully');
      setModalVisible(false);
      fetchUsers();
    } catch (error) {
      console.error('Error updating tokens:', error);
    }
  };

  return (
    <View style={styles.container}>
      <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
        <Image source={require('./assets/back-btn.png')} style={styles.backIcon} />
      </TouchableOpacity>
      <View style={styles.content}>
        <TextInput
          style={styles.searchInput}
          placeholder="Search by email or ID"
          value={searchQuery}
          onChangeText={setSearchQuery}
        />
        <Button title="Search" onPress={fetchUsers} />

        <FlatList
          data={users}
          keyExtractor={(item) => item.user_id}
          numColumns={2}
          renderItem={({ item }) => (
            <View style={styles.userGridItem}>
              <Text style={styles.userText}>ID: {item.user_id}</Text>
              <Text style={styles.userText}>Email: {item.email}</Text>
              <TouchableOpacity
                style={styles.selectButton}
                onPress={() => handleUserSelect(item)}
              >
                <Text style={styles.selectButtonText}>Select</Text>
              </TouchableOpacity>
            </View>
          )}
        />
      </View>

      <Modal visible={modalVisible} transparent={true} animationType="slide">
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Update Tokens for {selectedUser?.username}</Text>
            <TextInput
              style={styles.input}
              keyboardType="numeric"
              placeholder="Enter token amount"
              value={tokenChange}
              onChangeText={setTokenChange}
            />
            <Button title="Submit" onPress={handleTokenUpdate} />
            <Button title="Cancel" onPress={() => setModalVisible(false)} color="red" />
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },
  content: {
    width: '100%',
    maxWidth: 800,
    alignSelf: 'center',
  },
  backButton: {
    padding: 8,
    marginBottom: 20,
  },
  backIcon: {
    width: 24,
    height: 24,
  },
  searchInput: {
    height: 40,
    borderColor: '#ccc',
    borderWidth: 1,
    marginBottom: 10,
    paddingHorizontal: 10,
  },
  userGridItem: {
    flex: 1,
    padding: 15,
    backgroundColor: '#f0f0f0',
    margin: 5,
    borderRadius: 5,
    alignItems: 'center',
  },
  userText: {
    fontSize: 16,
  },
  selectButton: {
    marginTop: 10,
    backgroundColor: '#544564',
    padding: 8,
    borderRadius: 5,
  },
  selectButtonText: {
    color: '#fff',
    fontSize: 14,
  },
  modalContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  modalContent: {
    width: '80%',
    backgroundColor: 'white',
    padding: 20,
    borderRadius: 10,
    alignItems: 'center',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  input: {
    width: '100%',
    height: 40,
    borderColor: '#ccc',
    borderWidth: 1,
    marginBottom: 10,
    paddingHorizontal: 10,
  },
});

export default AdminUserManagementPage;
